import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

interface Notice {
  id: number;
  title: string;
  content: string;
  isImportant: boolean;
  publishedAt: string;
  createdAt: string;
}

const Home: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

      const response = await fetch(`${API_BASE_URL}/api/notice?page=1&pageSize=3`);

      if (!response.ok) {
        console.warn('Notice API returned non-OK status:', response.status);
        setNotices([]);
        return;
      }

      const result = await response.json();

      if (result.success && result.data) {
        setNotices(result.data);
      } else {
        setNotices([]);
      }
    } catch (error) {
      console.warn('Failed to fetch notices (this is normal if notice feature is not yet implemented):', error);
      setNotices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNoticeClick = (notice: Notice) => {
    setSelectedNotice(notice);
  };

  const closeModal = () => {
    setSelectedNotice(null);
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content fade-in">
            <h1 className="hero-title">
              배차 알림, 이제<br />
              <span className="highlight">자동으로 보내세요</span>
            </h1>
            <p className="hero-subtitle">
              EasyAutoCall과 함께 업무 효율을 70% 향상시키고<br />
              운영 비용을 절감하세요
            </p>
            <div className="hero-actions">
              <Link to="/tts-call" className="btn btn-primary btn-lg">
                지금 시작하기
              </Link>
              <Link to="/services" className="btn btn-outline btn-lg">
                서비스 둘러보기
              </Link>
            </div>
          </div>

          <div className="hero-image slide-in-right">
            <div className="floating-card">
              <div className="card-icon">📞</div>
              <div className="card-text">
                <div className="card-title">자동 발신</div>
                <div className="card-value">99.9%</div>
              </div>
            </div>
            <div className="floating-card delay-1">
              <div className="card-icon">📊</div>
              <div className="card-text">
                <div className="card-title">업무 효율</div>
                <div className="card-value">+70%</div>
              </div>
            </div>
            <div className="floating-card delay-2">
              <div className="card-icon">💰</div>
              <div className="card-text">
                <div className="card-title">콜당 요금</div>
                <div className="card-value">30원</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notices Section */}
      {!loading && notices.length > 0 && (
        <section className="notices section">
          <div className="container">
            <div className="section-header text-center">
              <h2 className="section-title">공지사항</h2>
            </div>

            <div className="notices-list">
              {notices.map((notice) => (
                <div
                  key={notice.id}
                  className={`notice-item ${notice.isImportant ? 'important' : ''}`}
                  onClick={() => handleNoticeClick(notice)}
                >
                  <div className="notice-header">
                    {notice.isImportant && <span className="badge-important">중요</span>}
                    <h3 className="notice-title">{notice.title}</h3>
                    <span className="notice-date">
                      {new Date(notice.publishedAt).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                  <p className="notice-preview">
                    {notice.content.length > 100
                      ? notice.content.substring(0, 100) + '...'
                      : notice.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Notice Modal */}
      {selectedNotice && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-header">
              {selectedNotice.isImportant && <span className="badge-important">중요</span>}
              <h2 className="modal-title">{selectedNotice.title}</h2>
              <span className="modal-date">
                {new Date(selectedNotice.publishedAt).toLocaleDateString('ko-KR')}
              </span>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-wrap' }}>{selectedNotice.content}</p>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <section className="features section">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title">핵심 기능</h2>
            <p className="section-subtitle">
              EasyAutoCall의 강력한 기능을 경험해보세요
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card fade-in">
              <div className="feature-icon">📱</div>
              <h3 className="feature-title">TTS 자동 전화</h3>
              <p className="feature-description">
                음성 메시지를 자동으로 발신하여<br />
                대량의 배차 알림을 손쉽게 전송
              </p>
              <Link to="/tts-call" className="feature-link">
                자세히 보기 →
              </Link>
            </div>

            <div className="feature-card fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="feature-icon">📋</div>
              <h3 className="feature-title">TTS 설문조사 / 배차 오토콜</h3>
              <p className="feature-description">
                전화로 자동 설문을 진행하고<br />
                실시간으로 응답 데이터 수집
              </p>
              <Link to="/tts-survey" className="feature-link">
                자세히 보기 →
              </Link>
            </div>

            <div className="feature-card fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="feature-icon">📊</div>
              <h3 className="feature-title">실시간 대시보드</h3>
              <p className="feature-description">
                발신 현황을 실시간으로 모니터링하고<br />
                상세한 통계와 리포트 제공
              </p>
              <Link to="/services" className="feature-link">
                자세히 보기 →
              </Link>
            </div>

            <div className="feature-card fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="feature-icon">🔄</div>
              <h3 className="feature-title">자동 재발신</h3>
              <p className="feature-description">
                미응답 시 자동으로 재발신하여<br />
                누락 없는 완벽한 알림 전달
              </p>
              <Link to="/services" className="feature-link">
                자세히 보기 →
              </Link>
            </div>

            <div className="feature-card fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="feature-icon">📁</div>
              <h3 className="feature-title">엑셀 일괄 업로드</h3>
              <p className="feature-description">
                배차표를 엑셀로 업로드하면<br />
                자동으로 일괄 발신 처리
              </p>
              <Link to="/services" className="feature-link">
                자세히 보기 →
              </Link>
            </div>

            <div className="feature-card fade-in" style={{ animationDelay: '0.5s' }}>
              <div className="feature-icon">💳</div>
              <h3 className="feature-title">합리적인 요금</h3>
              <p className="feature-description">
                콜당 30원의 저렴한 요금으로<br />
                부담 없이 사용 가능
              </p>
              <Link to="/pricing" className="feature-link">
                자세히 보기 →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">99.9%</div>
              <div className="stat-label">발신 성공률</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">30초</div>
              <div className="stat-label">평균 발신 시간</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">24/7</div>
              <div className="stat-label">무중단 서비스</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">70%</div>
              <div className="stat-label">업무 효율 향상</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">지금 바로 시작하세요</h2>
            <p className="cta-description">
              EasyAutoCall과 함께 업무 혁신을 경험해보세요
            </p>
            <Link to="/login" className="btn btn-primary btn-lg">
              무료로 시작하기
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
